/**
 * ShellBuilder: collects boxes and planes with a colour and a material class, then merges
 * everything per class into a handful of meshes with vertex colours. Ambient occlusion is
 * baked into the vertex colour analytically (corners near the floor, walls and ceiling darken),
 * which is the "vertex-baked AO computed in code" decision for Phases 1-6.
 */
import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';

export type MaterialClass = 'matte' | 'glass' | 'metal' | 'emissive' | 'floor';

export interface RoomBounds {
  minX: number;
  maxX: number;
  minZ: number;
  maxZ: number;
  height: number;
}

export interface PlaceOptions {
  colour: THREE.ColorRepresentation;
  cls?: MaterialClass;
  /** Rotation in radians around Y. */
  rotY?: number;
  /** Full Euler rotation; overrides rotY. */
  rotation?: THREE.Euler;
  /** Skip AO (for small emissive strips, signs). */
  noAo?: boolean;
}

interface Piece {
  geometry: THREE.BufferGeometry;
  cls: MaterialClass;
}

export const SHELL_MATERIALS: Record<MaterialClass, () => THREE.Material> = {
  matte: () =>
    new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.82, metalness: 0.02 }),
  floor: () =>
    new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.6, metalness: 0.03 }),
  glass: () =>
    new THREE.MeshStandardMaterial({
      vertexColors: true,
      roughness: 0.08,
      metalness: 0.1,
      transparent: true,
      opacity: 0.32,
      side: THREE.DoubleSide,
      depthWrite: false,
    }),
  metal: () =>
    // Satin silver: with no environment to reflect, high metalness rendered near black.
    new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.3, metalness: 0.25 }),
  emissive: () =>
    new THREE.MeshStandardMaterial({
      vertexColors: true,
      emissive: 0xffffff,
      emissiveIntensity: 0.9,
      roughness: 0.4,
      toneMapped: true,
    }),
};

const tmpColour = new THREE.Color();
const tmpNormal = new THREE.Vector3();
const tmpPos = new THREE.Vector3();

export class ShellBuilder {
  private pieces: Piece[] = [];
  readonly group = new THREE.Group();

  constructor(readonly bounds: RoomBounds) {}

  /** Axis-aligned box of size (w,h,d) centred at (x,y,z). */
  box(w: number, h: number, d: number, x: number, y: number, z: number, opts: PlaceOptions): this {
    const geometry = new THREE.BoxGeometry(w, h, d);
    return this.place(geometry, x, y, z, opts);
  }

  /** Cylinder with radius r and height h centred at (x,y,z). */
  cylinder(
    r: number,
    h: number,
    x: number,
    y: number,
    z: number,
    opts: PlaceOptions,
    segments = 20,
  ): this {
    const geometry = new THREE.CylinderGeometry(r, r, h, segments);
    return this.place(geometry, x, y, z, opts);
  }

  /** Plane (w,h) facing +Z by default, centred at (x,y,z). */
  plane(w: number, h: number, x: number, y: number, z: number, opts: PlaceOptions): this {
    const geometry = new THREE.PlaneGeometry(w, h);
    return this.place(geometry, x, y, z, opts);
  }

  place(geometry: THREE.BufferGeometry, x: number, y: number, z: number, opts: PlaceOptions): this {
    const g = geometry.index ? geometry.toNonIndexed() : geometry;
    if (g !== geometry) geometry.dispose();
    const m = new THREE.Matrix4();
    if (opts.rotation) m.makeRotationFromEuler(opts.rotation);
    else if (opts.rotY) m.makeRotationY(opts.rotY);
    m.setPosition(x, y, z);
    g.applyMatrix4(m);
    this.paint(g, opts.colour, opts.noAo ?? false, opts.cls ?? 'matte');
    for (const name of Object.keys(g.attributes))
      if (name !== 'position' && name !== 'normal' && name !== 'color') g.deleteAttribute(name);
    this.pieces.push({ geometry: g, cls: opts.cls ?? 'matte' });
    return this;
  }

  private paint(
    g: THREE.BufferGeometry,
    colour: THREE.ColorRepresentation,
    noAo: boolean,
    cls: MaterialClass,
  ): void {
    const pos = g.getAttribute('position');
    const nor = g.getAttribute('normal');
    const colours = new Float32Array(pos.count * 3);
    tmpColour.set(colour);
    for (let i = 0; i < pos.count; i++) {
      tmpPos.fromBufferAttribute(pos, i);
      tmpNormal.fromBufferAttribute(nor, i);
      const ao =
        noAo || cls === 'emissive' || cls === 'glass'
          ? 1
          : this.ambientOcclusion(tmpPos, tmpNormal);
      colours[i * 3] = tmpColour.r * ao;
      colours[i * 3 + 1] = tmpColour.g * ao;
      colours[i * 3 + 2] = tmpColour.b * ao;
    }
    g.setAttribute('color', new THREE.BufferAttribute(colours, 3));
  }

  /** Analytic AO: darker where surfaces meet the floor, the walls and the ceiling. */
  private ambientOcclusion(p: THREE.Vector3, n: THREE.Vector3): number {
    const b = this.bounds;
    const dFloor = Math.max(0, p.y);
    const dCeil = Math.max(0, b.height - p.y);
    const dWall = Math.max(0, Math.min(p.x - b.minX, b.maxX - p.x, p.z - b.minZ, b.maxZ - p.z));
    let occlusion = 0;
    const facingUp = n.y > 0.5;
    const facingDown = n.y < -0.5;
    if (facingUp) {
      // Floors and table tops darken near walls.
      occlusion += 0.32 * Math.exp(-dWall / 0.9);
    } else if (facingDown) {
      occlusion += 0.5 * Math.exp(-dFloor / 0.35) + 0.25 * Math.exp(-dWall / 0.8);
    } else {
      // Walls and furniture sides darken near the floor and ceiling.
      occlusion += 0.38 * Math.exp(-dFloor / 0.45) + 0.22 * Math.exp(-dCeil / 0.6);
      occlusion += 0.18 * Math.exp(-dWall / 0.5) * (1 - Math.abs(n.y));
    }
    return THREE.MathUtils.clamp(1 - occlusion, 0.35, 1);
  }

  /** Merges all pieces into one mesh per material class and returns the group. */
  build(): THREE.Group {
    const byClass = new Map<MaterialClass, THREE.BufferGeometry[]>();
    for (const piece of this.pieces) {
      if (!byClass.has(piece.cls)) byClass.set(piece.cls, []);
      byClass.get(piece.cls)!.push(piece.geometry);
    }
    for (const [cls, geometries] of byClass) {
      const merged = mergeGeometries(geometries, false);
      if (!merged) continue;
      merged.computeBoundingSphere();
      const mesh = new THREE.Mesh(merged, SHELL_MATERIALS[cls]());
      mesh.name = `shell-${cls}`;
      mesh.matrixAutoUpdate = false;
      if (cls === 'glass') mesh.renderOrder = 10;
      this.group.add(mesh);
      for (const g of geometries) g.dispose();
    }
    this.pieces = [];
    return this.group;
  }

  triangleCount(): number {
    let n = 0;
    this.group.traverse((o) => {
      const mesh = o as THREE.Mesh;
      if (mesh.isMesh) n += (mesh.geometry.getAttribute('position')?.count ?? 0) / 3;
    });
    return Math.round(n);
  }
}

/** Instanced copies of a geometry with one material and per-instance colour. */
export function instanced(
  geometry: THREE.BufferGeometry,
  material: THREE.Material,
  transforms: {
    position: THREE.Vector3Like;
    rotY?: number;
    scale?: number;
    colour?: THREE.ColorRepresentation;
  }[],
): THREE.InstancedMesh {
  const mesh = new THREE.InstancedMesh(geometry, material, transforms.length);
  const dummy = new THREE.Object3D();
  transforms.forEach((t, i) => {
    dummy.position.set(t.position.x, t.position.y, t.position.z);
    dummy.rotation.set(0, t.rotY ?? 0, 0);
    dummy.scale.setScalar(t.scale ?? 1);
    dummy.updateMatrix();
    mesh.setMatrixAt(i, dummy.matrix);
    if (t.colour !== undefined) mesh.setColorAt(i, tmpColour.set(t.colour));
  });
  mesh.instanceMatrix.needsUpdate = true;
  if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
  return mesh;
}
